import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ChartLineUp, FileText, Lightning, Shield, TrendDown, TrendUp,
    Sparkle, WhatsappLogo, ArrowRight, Check, UploadSimple, Files,
    FileArchive, CopySimple, Bank, Receipt
} from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import CampanaSociedadesBanner from "@/components/CampanaSociedadesBanner";
import { WHATSAPP_URL } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1674981208693-de5a9c4c4f44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc2NTI1MTk0fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1641060272821-df59e2c0b5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc2NTI1MTk0fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] } },
};

export default function Landing() {
    return (
        <div className="min-h-screen">
            <Navbar />
            <CampanaSociedadesBanner variant="compact" />
            <CampanaSociedadesBanner variant="full" />

            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-[#0B192C]/90" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 text-white">
                    <motion.div {...fadeUp} className="max-w-6xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="divider-gold" />
                            <span className="micro-label text-[var(--gf-gold)]">Más de 30 años de experiencia · Banca empresarial</span>
                        </div>
                        <h1 className="serif text-3xl md:text-5xl lg:text-6xl leading-[1.1] font-medium mb-4">
                            Tres herramientas. <span className="italic text-[var(--gf-gold)]">Un mismo panel.</span>
                        </h1>
                        <p className="text-sm md:text-base lg:text-lg text-white/75 leading-relaxed font-light">
                            Digitaliza y factura con Verifactu · Saca tus libros de IVA con IA · Interpreta tus balances y anticípate a Hacienda o al banco.
                        </p>
                    </motion.div>

                    {/* Tres productos lado a lado */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-10 max-w-6xl">
                        {/* OCR / Facturas */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.1 } }}>
                            <Link to="/portal-asesor" className="group block h-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/15 hover:border-[var(--gf-gold)] p-6 md:p-8 transition-all" data-testid="hero-card-facturas">
                                <div className="flex items-start justify-between mb-5">
                                    <div className="w-12 h-12 bg-[var(--gf-gold)]/20 flex items-center justify-center">
                                        <Files size={24} weight="light" className="text-[var(--gf-gold)]" />
                                    </div>
                                    <span className="micro-label text-[var(--gf-gold)]">01</span>
                                </div>
                                <h2 className="serif text-2xl md:text-3xl mb-3 leading-tight">OCR de Facturas y Libros de IVA</h2>
                                <p className="text-sm text-white/70 leading-relaxed mb-5">
                                    Sube un ZIP con tus PDFs. La IA extrae número, fecha, base, IVA y CIF, clasifica emitida o recibida y te da el libro de IVA en Excel importable en tu programa de contabilidad.
                                </p>
                                <div className="flex flex-wrap gap-2 text-[11px] text-white/50 mb-5">
                                    <span className="border border-white/20 px-2 py-0.5">OCR Vision</span>
                                    <span className="border border-white/20 px-2 py-0.5">Multi-empresa</span>
                                    <span className="border border-white/20 px-2 py-0.5">Excel importable</span>
                                </div>
                                <span className="text-sm text-[var(--gf-gold)] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                    Entrar al Portal <ArrowRight size={14} />
                                </span>
                            </Link>
                        </motion.div>

                        {/* Balances */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.2 } }}>
                            <Link to="/analisis/nuevo" className="group block h-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/15 hover:border-[var(--gf-gold)] p-6 md:p-8 transition-all" data-testid="hero-card-balances">
                                <div className="flex items-start justify-between mb-5">
                                    <div className="w-12 h-12 bg-[var(--gf-gold)]/20 flex items-center justify-center">
                                        <ChartLineUp size={24} weight="light" className="text-[var(--gf-gold)]" />
                                    </div>
                                    <span className="micro-label text-[var(--gf-gold)]">02</span>
                                </div>
                                <h2 className="serif text-2xl md:text-3xl mb-3 leading-tight">Análisis de balances</h2>
                                <p className="text-sm text-white/70 leading-relaxed mb-5">
                                    ¿El banco te dice NO por los números? Detectamos los puntos que tumban tu expediente bancario <strong className="text-white">antes</strong> de que el analista los vea, con las correcciones contables legales para darle la vuelta.
                                </p>
                                <div className="flex flex-wrap gap-2 text-[11px] text-white/50 mb-5">
                                    <span className="border border-white/20 px-2 py-0.5">Dictamen Pre-Bancario</span>
                                    <span className="border border-white/20 px-2 py-0.5">Informe Financiero</span>
                                    <span className="border border-white/20 px-2 py-0.5">Informe Master</span>
                                </div>
                                <span className="text-sm text-[var(--gf-gold)] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                    Generar informe financiero <ArrowRight size={14} />
                                </span>
                            </Link>
                        </motion.div>

                        {/* Verifactu · Facturación */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.3 } }}>
                            <Link to="/verifactu" className="group block h-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/15 hover:border-[var(--gf-gold)] p-6 md:p-8 transition-all" data-testid="hero-card-verifactu">
                                <div className="flex items-start justify-between mb-5">
                                    <div className="w-12 h-12 bg-[var(--gf-gold)]/20 flex items-center justify-center">
                                        <Receipt size={24} weight="light" className="text-[var(--gf-gold)]" />
                                    </div>
                                    <span className="micro-label text-[var(--gf-gold)]">03</span>
                                </div>
                                <h2 className="serif text-2xl md:text-3xl mb-3 leading-tight">Facturación Verifactu</h2>
                                <p className="text-sm text-white/70 leading-relaxed mb-5">
                                    Emite facturas conforme al RD 1007/2023. Numeración correlativa, huella encadenada, QR de comprobación AEAT y PDF listo para enviar. Sin salir del panel.
                                </p>
                                <div className="flex flex-wrap gap-2 text-[11px] text-white/50 mb-5">
                                    <span className="border border-white/20 px-2 py-0.5">RD 1007/2023</span>
                                    <span className="border border-white/20 px-2 py-0.5">QR + Huella</span>
                                    <span className="border border-white/20 px-2 py-0.5">Multi-empresa</span>
                                </div>
                                <span className="text-sm text-[var(--gf-gold)] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                    Emitir factura <ArrowRight size={14} />
                                </span>
                            </Link>
                        </motion.div>
                    </div>

                    {/* CTA unificado */}
                    <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-white/15 pt-6 max-w-6xl">
                        <Link to="/registro" className="btn-gold" data-testid="hero-cta-register">
                            Crear cuenta gratis <ArrowRight size={16} />
                        </Link>
                        <span className="text-sm text-white/60">Un único acceso para todas las herramientas.</span>
                        <Link to="/demo" className="text-sm text-white/60 hover:text-white ml-auto" data-testid="hero-cta-demo">
                            Ver informe de demostración →
                        </Link>
                    </div>
                </div>
            </section>

            {/* FEATURES · Balances */}
            <section id="caracteristicas" className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="features-section">
                <div className="max-w-3xl mb-16">
                    <div className="micro-label mb-4">— I · Análisis de balances</div>
                    <h2 className="serif text-4xl md:text-5xl leading-tight">
                        Un informe financiero<br />
                        digno de una <span className="italic text-[var(--gf-gold)]">firma boutique</span>.
                    </h2>
                    <p className="text-[var(--gf-text-secondary)] mt-4 max-w-2xl">
                        Interpretación experta de tu balance, ratios, alertas fiscales y consejos accionables — en 60 segundos.
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-px bg-[var(--gf-border)]">
                    {[
                        { icon: FileText, n: "01", t: "Entrada flexible", d: "Sube PDF, Excel o CSV de tu balance, o rellena el formulario manual con los campos del PGC." },
                        { icon: ChartLineUp, n: "02", t: "Ratios financieros", d: "Liquidez, prueba ácida, solvencia, endeudamiento, autonomía, ROE, ROA y margen neto." },
                        { icon: Sparkle, n: "03", t: "Interpretación IA", d: "Narrativa contable en español elaborada con GPT-5.2 sobre la salud financiera de tu empresa." },
                        { icon: Lightning, n: "04", t: "Consejos accionables", d: "Recomendaciones estratégicas priorizadas y alertas fiscales específicas del PGC." },
                        { icon: TrendUp, n: "05", t: "Comparación histórica", d: "Compara dos periodos lado a lado para detectar tendencias y desviaciones." },
                        { icon: Shield, n: "06", t: "Privacidad total", d: "Tus datos financieros se almacenan cifrados y nunca se comparten con terceros." },
                    ].map((f) => (
                        <div key={f.n} className="bg-white p-10 hover:bg-[var(--gf-subtle)] transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="mono text-xs text-[var(--gf-gold)]">{f.n}</div>
                                <div className="flex-1">
                                    <f.icon size={32} weight="light" className="mb-4 text-[var(--gf-navy)]" />
                                    <h3 className="serif text-2xl mb-3">{f.t}</h3>
                                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{f.d}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURES · Facturas + Libros IVA */}
            <section id="digitalizacion" className="bg-[var(--gf-subtle)]" data-testid="digitalizacion-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
                    <div className="max-w-3xl mb-16">
                        <div className="micro-label mb-4">— II · Digitalización · Libros de IVA</div>
                        <h2 className="serif text-4xl md:text-5xl leading-tight">
                            De un <span className="italic text-[var(--gf-gold)]">ZIP de facturas</span><br />
                            al libro de IVA en Excel.
                        </h2>
                        <p className="text-[var(--gf-text-secondary)] mt-4 max-w-2xl">
                            Sube un ZIP con tus PDFs o imágenes. La IA extrae número, fecha, base, IVA, retenciones y CIF de cada factura, los clasifica por empresa y ejercicio y te devuelve el libro de emitidas y recibidas listo para importar en tu programa de contabilidad.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-[var(--gf-border)]">
                        {[
                            { icon: UploadSimple, n: "01", t: "Sube un ZIP", d: "Arrastra un ZIP con tus PDFs o imágenes de facturas. Multi-empresa: cada ZIP puede ir a la sociedad que quieras." },
                            { icon: Sparkle, n: "02", t: "OCR con IA Vision", d: "La IA lee número, fecha, emisor, receptor, base, tipo y cuota de IVA, retenciones y total, y clasifica emitida o recibida." },
                            { icon: CopySimple, n: "03", t: "Avisos automáticos", d: "Marca duplicadas por nº+fecha+emisor, IVA que no cuadra con base·%, retenciones IRPF no capturadas y CIF que no coinciden." },
                            { icon: FileArchive, n: "04", t: "Libros de IVA en Excel", d: "Descarga el libro de Emitidas y Recibidas por ejercicio, con totales por trimestre — listo para presentar o auditar." },
                            { icon: Bank, n: "05", t: "Importable en tu contabilidad", d: "El Excel sigue una estructura estándar compatible con la importación en programas contables habituales (A3, Contasol, Sage, ContaPlus…)." },
                            { icon: Files, n: "06", t: "ZIP reorganizado", d: "Además del Excel, descarga un ZIP con las facturas ordenadas en carpetas Cliente / Empresa / Ejercicio / Emitidas·Recibidas para tu archivo." },
                        ].map((f) => (
                            <div key={f.n} className="bg-white p-10 hover:bg-[var(--gf-subtle)] transition-colors">
                                <div className="flex items-start gap-4">
                                    <div className="mono text-xs text-[var(--gf-gold)]">{f.n}</div>
                                    <div className="flex-1">
                                        <f.icon size={32} weight="light" className="mb-4 text-[var(--gf-navy)]" />
                                        <h3 className="serif text-2xl mb-3">{f.t}</h3>
                                        <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{f.d}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12 flex flex-wrap gap-3">
                        <Link to="/portal-asesor" className="btn-gold" data-testid="cta-portal-asesor-landing">
                            <UploadSimple size={16} weight="bold" /> Probar digitalización
                        </Link>
                    </div>
                </div>
            </section>

            {/* QUICK CHECK - gancho de conversión (requiere registro) */}
            <section id="prueba-gratis" className="bg-white border-y border-[var(--gf-border)]" data-testid="quickcheck-cta-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="micro-label mb-4">— Créditos de bienvenida</div>
                        <h2 className="serif text-4xl md:text-5xl leading-tight mb-6">
                            50 créditos<br />
                            <span className="italic text-[var(--gf-gold)]">de regalo al registrarte.</span>
                        </h2>
                        <p className="text-[var(--gf-text-secondary)] leading-relaxed mb-6">
                            1 € = 1 crédito. Regístrate con tu nombre, apellidos, teléfono y email y recibe 50 créditos
                            para estrenar la herramienta sin coste. Después recargas sólo lo que necesites.
                        </p>
                        <ul className="space-y-3 mb-8">
                            {[
                                "50 créditos gratis al registrarte · sin tarjeta ni permanencia",
                                "Dictamen Pre-Bancario 14 · Informe Financiero 24 · Informe Master 44 créditos",
                                "Acceso también al OCR de facturas, libros de IVA y Verifactu",
                            ].map((t) => (
                                <li key={t} className="flex items-start gap-3 text-sm">
                                    <Check size={18} className="text-[var(--gf-gold)] mt-0.5 flex-shrink-0" weight="bold" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/registro" className="btn-gold" data-testid="quickcheck-cta-register">
                            Crear cuenta y recibir 50 créditos <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="border border-[var(--gf-border)] bg-[var(--gf-subtle)] p-8 md:p-10">
                        <div className="mono text-xs text-[var(--gf-text-muted)] mb-4">— Vista previa del diagnóstico</div>
                        <div className="space-y-4">
                            {[
                                { l: "Liquidez", v: "1,42", ok: true },
                                { l: "Endeudamiento", v: "68 %", ok: false },
                                { l: "Autonomía financiera", v: "32 %", ok: true },
                                { l: "ROE", v: "9,4 %", ok: true },
                            ].map((r) => (
                                <div key={r.l} className="flex items-center justify-between border-b border-[var(--gf-border)]/50 pb-3">
                                    <span className="text-sm text-[var(--gf-text-secondary)]">{r.l}</span>
                                    <span className={`mono text-sm font-semibold ${r.ok ? "text-emerald-700" : "text-amber-700"}`}>{r.v}</span>
                                </div>
                            ))}
                            <div className="pt-2 flex items-center gap-2 text-xs text-[var(--gf-text-muted)]">
                                <Sparkle size={14} className="text-[var(--gf-gold)]" weight="fill" />
                                <span>Y una interpretación en 3 párrafos con recomendaciones específicas.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section className="bg-[var(--gf-subtle)]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-16 items-center">
                    <motion.div {...fadeUp}>
                        <img src={ABOUT_IMG} alt="Oficina" className="w-full h-[500px] object-cover" />
                    </motion.div>
                    <div>
                        <div className="micro-label mb-4">— III · Metodología</div>
                        <h2 className="serif text-4xl md:text-5xl leading-tight mb-6">
                            Del dato al <span className="italic text-[var(--gf-gold)]">criterio</span>.
                        </h2>
                        <p className="text-lg leading-relaxed text-[var(--gf-text-secondary)] mb-6">
                            Combinamos el cálculo riguroso de ratios financieros con el criterio narrativo
                            de un asesor fiscal experimentado. No te entregamos un número: te entregamos
                            <span className="italic text-[var(--gf-navy)]"> una lectura</span>.
                        </p>
                        <ul className="space-y-4 mb-8">
                            {[
                                "Interpretación narrativa en tono asesor experto",
                                "Consejos priorizados: alta, media, baja",
                                "Alertas fiscales específicas del PGC español",
                                "Comparación entre ejercicios y análisis de tendencia",
                            ].map((x) => (
                                <li key={x} className="flex items-start gap-3">
                                    <Check size={20} className="text-[var(--gf-gold)] mt-0.5 flex-shrink-0" weight="bold" />
                                    <span className="text-[var(--gf-text)]">{x}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* PLANES */}
            <section id="planes" className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="plans-section">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <div className="micro-label mb-4">— III · Productos</div>
                    <h2 className="serif text-4xl md:text-5xl leading-tight">
                        Paga por informe.<br />Sin suscripciones.
                    </h2>
                    <p className="text-sm text-[var(--gf-text-secondary)] mt-6 leading-relaxed">
                        Sin packs, sin caducidad, sin compromisos. Pides un informe cuando lo necesitas.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    <PlanCard
                        tier="Dictamen Pre-Bancario"
                        price="14€"
                        period="+ IVA"
                        accent
                        features={[
                            "16,94 € IVA incluido",
                            "9 ratios bancarios + veredicto verde/ámbar/rojo",
                            "Alertas críticas detectadas",
                            "Recomendaciones contables legales",
                            "Simulador Antes / Después incluido",
                        ]}
                        cta="Pedir Dictamen Pre-Bancario"
                        href="/registro?next=/maquina-verdad"
                    />
                    <PlanCard
                        tier="Balance Básico"
                        price="24€"
                        period="+ IVA"
                        features={[
                            "29,04 € IVA incluido",
                            "Interpretación IA + 8 ratios clave",
                            "Alertas críticas + recomendaciones",
                            "Simulador Antes / Después",
                            "Exportación PDF profesional",
                        ]}
                        cta="Contratar Balance Básico"
                        href="/registro?next=/pagos&plan=balance_basico_1"
                    />
                    <PlanCard
                        tier="Balance Master"
                        price="44€"
                        period="+ IVA"
                        highlighted
                        features={[
                            "53,24 € IVA incluido",
                            "Todo lo del Básico",
                            "Auditoría bancaria (9 ratios + Deuda/EBITDA + DSCR)",
                            "Balance rectificado antes/después",
                            "Interpretación IA larga · plan de acción",
                        ]}
                        cta="Contratar Balance Master"
                        href="/registro?next=/pagos&plan=balance_master_1"
                        badge="Recomendado"
                    />
                </div>

                <div className="text-center mt-10">
                    <p className="text-xs text-[var(--gf-text-muted)] italic">
                        1 € = 1 crédito · Al registrarte recibes <strong className="text-[var(--gf-navy)]">50 créditos de bienvenida</strong> para estrenar cualquiera de los tres informes.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[var(--gf-navy)] text-white" data-testid="cta-section">
                <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 text-center">
                    <div className="divider-gold mx-auto mb-6" />
                    <h2 className="serif text-4xl md:text-5xl mb-6">
                        Analiza tu primer balance <span className="italic text-[var(--gf-gold)]">en 2 minutos</span>.
                    </h2>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">
                        Regístrate gratis y obtén al instante un informe financiero profesional sobre el balance de tu empresa.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Link to="/registro" className="btn-gold" data-testid="cta-register">
                            Comenzar ahora <ArrowRight size={18} />
                        </Link>
                        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-outline" style={{ borderColor: "white", color: "white" }}>
                            <WhatsappLogo size={18} weight="fill" /> Hablar con nosotros
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function PlanCard({ tier, price, period, features, highlighted, accent, master, cta, href, badge }) {
    const borderTop = highlighted
        ? "border-t-4 border-t-[var(--gf-navy)]"
        : accent
        ? "border-t-4 border-t-[var(--gf-gold)]"
        : master
        ? "border-t-4 border-t-[var(--gf-navy)]"
        : "border-t-2 border-t-[var(--gf-border)]";
    const bgClass = master ? "bg-[var(--gf-navy)] text-white" : "bg-white";
    return (
        <div className={`${bgClass} p-6 border border-[var(--gf-border)] ${borderTop} relative flex flex-col`} data-testid={`plan-${tier.toLowerCase().replace(/\s/g, "-")}`}>
            {badge && (
                <div className={`absolute -top-3 left-6 ${master ? "bg-[var(--gf-gold)]" : "bg-[var(--gf-gold)]"} text-white px-3 py-1 text-[10px] tracking-widest uppercase`}>
                    {badge}
                </div>
            )}
            <div className={`micro-label mb-3 ${master ? "text-white/70" : ""}`}>{tier}</div>
            <div className="mb-6">
                <span className="editorial-num">{price}</span>
                <span className={`text-sm ${master ? "text-white/60" : "text-[var(--gf-text-muted)]"} ml-2`}>{period}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                        <Check size={16} className={`${master ? "text-[var(--gf-gold)]" : "text-[var(--gf-gold)]"} mt-0.5 flex-shrink-0`} weight="bold" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <Link to={href} className={highlighted || accent || master ? `${master ? "btn-gold" : "btn-primary"} justify-center` : "btn-outline justify-center"} data-testid={`plan-${tier.toLowerCase().replace(/\s/g, "-")}-cta`}>
                {cta}
            </Link>
        </div>
    );
}
